var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInteractor, useModel } from '@veeam/core';
import { GridBodyLayout } from "../layouts";
import { GRID_COLUMN_JUSTIFY } from "../Grid";
import { DataGridInteractor } from './DataGridInteractor';
import { useDataGridNotification } from './hooks';
import { useContextMenu } from "../smart/collectionContextMenu";
import { buildCollectionToolbar } from "../smart/collectionToolbar";
import { LargeInfoStatusIcon } from "../icons";
import { RefreshSvg } from './icons/RefreshSvg';
import { IgnoreSvg } from './icons/IgnoreSvg';
import { COLUMN_CONTENT_JUSTIFY, GridCore } from "../GridCore";
import { gridHeaderCell } from "../GridCore/renderers/gridHeaderCell";
import { gridNormalPlugin } from "../GridCore/plugins/normal";
import { gridNormalCell } from "../GridCore/renderers/gridNormalCell";
import { Pagination } from "../controls";
import { gridSingleSelectionPlugin } from "../GridCore/plugins/singleSelection";
import { gridMultiSelectionPlugin } from "../GridCore/plugins/multiSelection";
import { gridColumnResizePlugin } from "../GridCore/plugins/columnResize/gridColumnResizePlugin";
import { gridColumnShownStatePlugin } from "../GridCore/plugins/columnShownState";
import { gridNotificationPlugin } from "../GridCore/plugins/notification";
import { gridSubheaderPlugin } from "../GridCore/plugins/subheader";
import { gridColumnMovingPlugin } from "../GridCore/plugins/columnMoving";
import { useDisableScroll } from "../helpers/useDisableScroll";
export function DataGridNew(_a) {
    var model = _a.model, disabled = _a.disabled, loading = _a.loading, toolbar = _a.toolbar, columns = _a.columns, config = _a.config, _b = _a.flags, flags = _b === void 0 ? {} : _b, texts = _a.texts, fragment = _a.fragment, isRowDisabled = _a.isRowDisabled, isRowInactive = _a.isRowInactive, apiController = _a.apiController, disabledSelection = _a.disabledSelection, onRowClick = _a.onRowClick;
    var _c, _d, _e, _f, _g;
    var _h = useDataGridNotification(), showNotification = _h.showNotification, dismissNotification = _h.dismissNotification, notificationContent = _h.notificationContent, canCloseNotification = _h.canCloseNotification;
    var gridApi = {
        showNotification: showNotification,
        dismissNotification: dismissNotification
    };
    var interactor = useInteractor(function () {
        var _a;
        return new DataGridInteractor(model, {
            autoLoad: (_a = config === null || config === void 0 ? void 0 : config.autoLoad) !== null && _a !== void 0 ? _a : true,
            persistent: config === null || config === void 0 ? void 0 : config.persistent,
            hasNoneSort: config === null || config === void 0 ? void 0 : config.hasNoneSort,
            isRowDisabled: isRowDisabled
        });
    }).interactor;
    interactor.presenter = {
        showOutdatedNotification: function () {
            gridApi === null || gridApi === void 0 ? void 0 : gridApi.showNotification({
                icon: React.createElement(LargeInfoStatusIcon, null),
                content: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationMessage) || 'Grid data is outdated',
                onDidClose: function () {
                    interactor.didSelectIgnoreFreshData();
                },
                actions: [
                    {
                        icon: React.createElement(RefreshSvg, null),
                        text: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationRefreshAction) || 'Refresh',
                        handler: function () {
                            interactor.didSelectApplyFreshData();
                        }
                    },
                    {
                        icon: React.createElement(IgnoreSvg, null),
                        text: (texts === null || texts === void 0 ? void 0 : texts.outdatedNotificationIgnoreAction) || 'Ignore',
                        handler: function () { return interactor.didSelectIgnoreFreshData(); }
                    }
                ]
            });
        },
        dismissNotification: function () {
            gridApi.dismissNotification();
        }
    };
    var setResizeState = useState({})[1];
    var setOrderState = useState({})[1];
    var _j = useState({}), columnShownState = _j[0], setColumnShownState = _j[1];
    var idGetter = useCallback(function (item) { return model.getItemID(item); }, []);
    var _k = useModel(model), value = _k.value, meta = _k.meta;
    var data = value.items;
    if (apiController) {
        apiController.injectApi(gridApi);
    }
    useEffect(function () {
        return function () {
            apiController === null || apiController === void 0 ? void 0 : apiController.destroy();
        };
    }, []);
    var ref = useRef(null);
    useDisableScroll({
        activate: disabled,
        ref: ref
    });
    var persistConfig = interactor.getPersistConfig();
    var internalLoading = loading || meta.loading;
    var sorting = value.sorting[0];
    var columnsConfig = columns.map(function (column) {
        var _a;
        return {
            id: column.id,
            width: (_a = persistConfig.columnSizes[column.id]) !== null && _a !== void 0 ? _a : column.width,
            order: persistConfig.columnOrders[column.id],
            minWidth: column.minWidth,
            justify: column.justify === GRID_COLUMN_JUSTIFY.right
                ? COLUMN_CONTENT_JUSTIFY.end
                : COLUMN_CONTENT_JUSTIFY.start,
            header: gridHeaderCell({
                title: column.title,
                onClick: column.sortKey && !column.onHeaderClick
                    ? function () {
                        column.sortKey && interactor.toggleSort(column.sortKey);
                    }
                    : column.onHeaderClick,
                sorting: (sorting === null || sorting === void 0 ? void 0 : sorting.key) && column.sortKey === (sorting === null || sorting === void 0 ? void 0 : sorting.key) ? sorting === null || sorting === void 0 ? void 0 : sorting.direction : undefined
            }),
            cell: function (payload) {
                return gridNormalCell({
                    value: function () {
                        return column.cell(payload.rowData, {
                            rowData: payload.rowData,
                            rowIndex: payload.rowIndex,
                            columnWidth: payload.column.width,
                            justify: payload.column.justify === COLUMN_CONTENT_JUSTIFY.end
                                ? GRID_COLUMN_JUSTIFY.right
                                : GRID_COLUMN_JUSTIFY.left
                        });
                    }
                })(payload);
            }
        };
    });
    var selectedItems = [];
    // const isSelectionConsistent = true;
    for (var i = 0; i < value.selection.length; i++) {
        var itemId = value.selection[i];
        var item = model.getItemById(itemId);
        if (!item) {
            continue;
        }
        if (isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
            rowData: item,
            rowIndex: i,
            id: itemId
        })) {
            continue;
        }
        selectedItems.push(item);
    }
    var renderPagination = function () {
        if ((config === null || config === void 0 ? void 0 : config.forcePaginationShownState) === false) {
            return null;
        }
        if ((interactor.hasPagination() || (config === null || config === void 0 ? void 0 : config.forcePaginationShownState)) && value.pagination) {
            var activePage = value.pagination.offset / value.pagination.limit + 1;
            return (React.createElement(Pagination, { style: { alignSelf: 'center' }, activePage: activePage, lastPage: Math.ceil((value.totalCount || data.length) / value.pagination.limit), disabled: disabled, onPageChange: interactor.didPageChange.bind(interactor), onPageClick: interactor.didPageChange.bind(interactor) }));
        }
        return null;
    };
    var _l = buildCollectionToolbar({
        config: toolbar,
        model: model,
        buildIn: config === null || config === void 0 ? void 0 : config.buildIn,
        payloadGetter: function () { return ({
            selectedItems: selectedItems,
            loading: internalLoading,
            flags: flags
        }); }
    }), toolbarElement = _l.element, contextMenuActions = _l.contextMenuActions;
    var contextMenu = useContextMenu({
        items: contextMenuActions.map(function (item) {
            return {
                id: item.id,
                title: item.title || '',
                icon: item.icon,
                decorators: item.decorators,
                disabled: item.disabled,
                hidden: item.hidden,
                handler: function () { var _a; return (_a = item.handler) === null || _a === void 0 ? void 0 : _a.call(item); }
            };
        }),
        payloadGetter: function () { return ({ selectedItems: selectedItems, loading: internalLoading, flags: flags }); }
    });
    var plugins = [
        gridNormalPlugin({
            headless: config === null || config === void 0 ? void 0 : config.headless,
            rowHeightGetter: (config === null || config === void 0 ? void 0 : config.rowHeight) ? function () { return (config === null || config === void 0 ? void 0 : config.rowHeight) || 0; } : undefined,
            isLoading: internalLoading,
            isEmpty: !internalLoading && !data.length,
            emptyRenderer: config === null || config === void 0 ? void 0 : config.emptyRenderer,
            disabled: disabled,
            isRowInactive: function (index) {
                var _a;
                return ((_a = isRowInactive === null || isRowInactive === void 0 ? void 0 : isRowInactive({
                    rowIndex: index,
                    rowData: model.getValue().items[index],
                    id: model.getItemID(model.getValue().items[index])
                })) !== null && _a !== void 0 ? _a : false);
            }
        })
    ];
    if (config === null || config === void 0 ? void 0 : config.canResizeColumns) {
        plugins.push(gridColumnResizePlugin({
            onResize: function (payload) {
                Object.entries(payload).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    interactor.didColumnSizeChange({
                        columnId: String(key),
                        width: value
                    });
                });
                setResizeState(payload);
            }
        }));
    }
    if (notificationContent) {
        plugins.push(gridNotificationPlugin({
            onRequestClose: canCloseNotification ? dismissNotification : undefined,
            content: notificationContent
        }));
    }
    if (config === null || config === void 0 ? void 0 : config.columnsToggleable) {
        var menu = [];
        var _loop_1 = function (i) {
            var col = columns[i];
            if ((_c = col.canHidden) !== null && _c !== void 0 ? _c : true) {
                var shownStateValue_1 = (_e = (_d = columnShownState[col.id]) !== null && _d !== void 0 ? _d : persistConfig.columnVisibility[col.id]) !== null && _e !== void 0 ? _e : !col.hidden;
                menu.push({
                    id: col.id,
                    title: col.title,
                    value: shownStateValue_1,
                    onClick: function () {
                        var _a, _b;
                        var _c;
                        interactor.didColumnVisibilityChange({
                            columnId: col.id,
                            isShown: !shownStateValue_1
                        });
                        var columnOrders = interactor.getPersistConfig().columnOrders;
                        interactor.didColumnOrderChange(__assign(__assign({}, columnOrders), (_a = {}, _a[col.id] = (_c = columnOrders[col.id]) !== null && _c !== void 0 ? _c : i, _a)));
                        setColumnShownState(__assign(__assign({}, columnShownState), (_b = {}, _b[col.id] = !shownStateValue_1, _b)));
                    }
                });
            }
        };
        for (var i = 0; i < columns.length; i++) {
            _loop_1(i);
        }
        plugins.push(gridColumnShownStatePlugin({
            menu: menu
        }));
    }
    var isSelectionNotDefined = typeof (config === null || config === void 0 ? void 0 : config.selection) === 'undefined';
    var isSelectionDefinedAsSingle = (config === null || config === void 0 ? void 0 : config.selection) === true ||
        (typeof (config === null || config === void 0 ? void 0 : config.selection) !== 'boolean' && !((_f = config === null || config === void 0 ? void 0 : config.selection) === null || _f === void 0 ? void 0 : _f.multi));
    if ((disabledSelection && isSelectionNotDefined) || isSelectionDefinedAsSingle) {
        plugins.push(gridSingleSelectionPlugin({
            isSelected: function (index) {
                var selectedId = model.getValue().selection[0];
                if (typeof selectedId === 'undefined') {
                    return false;
                }
                var item = model.getValue().items[index];
                var itemId = model.getItemID(item);
                if (isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
                    rowData: item,
                    rowIndex: index,
                    id: itemId
                })) {
                    return false;
                }
                return selectedId === itemId;
            },
            onRowClick: function (index) {
                var rowData = model.getValue().items[index];
                onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick({
                    rowIndex: index,
                    rowData: rowData,
                    id: model.getItemID(rowData)
                });
            },
            onSelect: function (index) {
                if (disabledSelection) {
                    return;
                }
                interactor.didSelectionChange([model.getItemID(model.getValue().items[index])]);
            },
            onContextMenu: function (index, e) {
                interactor.didSelectionChange([model.getItemID(model.getValue().items[index])]);
                contextMenu.show({
                    offsetX: e.pageX,
                    offsetY: e.pageY
                });
            }
        }));
    }
    if ((config === null || config === void 0 ? void 0 : config.selection) && typeof config.selection !== 'boolean' && config.selection.multi) {
        plugins.push(gridMultiSelectionPlugin({
            hasCheckbox: (_g = config.selection.withCheckbox) !== null && _g !== void 0 ? _g : true,
            subheader: config.selection.subheaderRenderer === null
                ? null
                : config.selection.subheaderRenderer || {
                    selected: model.getValue().selection.length,
                    total: model.getValue().totalCount || model.getValue().items.length
                },
            onRowClick: function (index) {
                var rowData = model.getValue().items[index];
                onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick({
                    rowIndex: index,
                    rowData: rowData,
                    id: model.getItemID(rowData)
                });
            },
            onMultiSelect: function (index, isShift) {
                if (disabledSelection) {
                    return;
                }
                var value = model.getValue().selection;
                var itemId = model.getItemID(model.getValue().items[index]);
                if (isShift && value.length) {
                    var edgeItemSelectedIndex = model
                        .getValue()
                        .items.findIndex(function (i) { return model.getItemID(i) === value[value.length - 1]; });
                    var start = Math.min(edgeItemSelectedIndex, index);
                    var end = Math.max(edgeItemSelectedIndex, index);
                    var nextValue_1 = [];
                    for (var i = start; i <= end; i++) {
                        nextValue_1.push(model.getItemID(model.getValue().items[i]));
                    }
                    if (index > edgeItemSelectedIndex) {
                        nextValue_1 = nextValue_1.reverse();
                    }
                    model.update(function (prevValue) {
                        prevValue.selection = nextValue_1;
                    });
                    return;
                }
                if (value.includes(model.getItemID(model.getValue().items[index]))) {
                    model.update(function (prevValue) {
                        prevValue.selection = value.filter(function (v) { return v !== itemId; });
                    });
                    return;
                }
                model.update(function (prevValue) {
                    prevValue.selection = __spreadArrays(value, [itemId]);
                });
            },
            onSelect: function (index) {
                if (disabledSelection) {
                    return;
                }
                model.update(function (prevValue) {
                    prevValue.selection = [model.getItemID(model.getValue().items[index])];
                });
            },
            onContextMenu: function (index, e) {
                if (disabledSelection) {
                    return;
                }
                var selectedId = model.getItemID(model.getValue().items[index]);
                var isNotSelected = !model.getValue().selection.includes(selectedId);
                if (isNotSelected) {
                    model.update(function (prevValue) {
                        prevValue.selection = [selectedId];
                    });
                }
                contextMenu.show({
                    offsetX: e.pageX,
                    offsetY: e.pageY
                });
            },
            onAllSelect: function () {
                if (disabledSelection) {
                    return;
                }
                interactor.didSelectAllChange();
            },
            isAllSelected: function () {
                return interactor.isSelectAll();
            },
            isSelected: function (index) {
                var selectedId = model.getValue().selection[0];
                if (typeof selectedId === 'undefined') {
                    return false;
                }
                var item = model.getValue().items[index];
                var itemId = model.getItemID(item);
                if (isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
                    rowData: item,
                    rowIndex: index,
                    id: itemId
                })) {
                    return false;
                }
                return model
                    .getValue()
                    .selection.includes(model.getItemID(model.getValue().items[index]));
            },
            isRowDisabled: function (index) {
                var _a;
                return ((_a = isRowDisabled === null || isRowDisabled === void 0 ? void 0 : isRowDisabled({
                    rowIndex: index,
                    rowData: model.getValue().items[index],
                    id: model.getItemID(model.getValue().items[index])
                })) !== null && _a !== void 0 ? _a : false);
            }
        }));
    }
    if (config === null || config === void 0 ? void 0 : config.subheader) {
        plugins.push(gridSubheaderPlugin({
            content: config === null || config === void 0 ? void 0 : config.subheader.render()
        }));
    }
    if (config === null || config === void 0 ? void 0 : config.canOrderedColumns) {
        plugins.push(gridColumnMovingPlugin({
            onChange: function (_a) {
                var orders = _a.orders;
                interactor.didColumnOrderChange(orders);
                setOrderState(orders);
            }
        }));
    }
    return (React.createElement(GridBodyLayout, { ref: ref, toolbar: toolbarElement, fragment: React.createElement(React.Fragment, null,
            Array.isArray(toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment)
                ? toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment.map(function (i, idx) { return (React.createElement(React.Fragment, { key: idx }, i)); }) : toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment,
            contextMenu.element,
            fragment), grid: React.createElement(GridCore, { idGetter: idGetter, data: model.getValue().items, columns: columnsConfig, plugins: plugins, footer: renderPagination() }) }));
}
//# sourceMappingURL=DataGridNew.js.map